import http from '../const/http-common';

class DimensionDefaultsService {
  /**
   * Get all dimension defaults with pagination
   * @param {number} page - Page number (default: 1)
   * @param {number} pageSize - Page size (default: 10)
   * @returns {Promise} - API response
   */
  async getDimensionDefaults(page = 1, pageSize = 10) {
    try {
      const response = await http.get('/v1/dimension-defaults/', {
        params: { page, page_size: pageSize }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting dimension defaults:', error);
      throw error;
    }
  }

  /**
   * Get dimension defaults for a specific cube
   * @param {number} cubeId - The cube ID
   * @returns {Promise} - API response
   */
  async getDimensionDefaultsByCube(cubeId) {
    try {
      const response = await http.get(`/v1/dimension-defaults/cube/${cubeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting dimension defaults by cube:', error);
      throw error;
    }
  }

  /**
   * Get dimension default by ID
   * @param {number} id - The dimension default ID
   * @returns {Promise} - API response
   */
  async getDimensionDefault(id) {
    try {
      const response = await http.get(`/v1/dimension-defaults/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting dimension default:', error);
      throw error;
    }
  }

  /**
   * Get dimension default by cube and dimension
   * @param {number} cubeId - The cube ID
   * @param {number} dimensionId - The dimension (oracle_epm_metadata_id)
   * @returns {Promise} - API response
   */
  async getDimensionDefaultByCubeAndDimension(cubeId, dimensionId) {
    try {
      const response = await http.get(
        `/v1/dimension-defaults/cube/${cubeId}/dimension/${dimensionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting dimension default by cube and dimension:', error);
      throw error;
    }
  }

  /**
   * Create a dimension default
   * @param {Object} data - The dimension default data
   * @returns {Promise} - API response
   */
  async createDimensionDefault(data) {
    try {
      const response = await http.post('/v1/dimension-defaults/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating dimension default:', error);
      throw error;
    }
  }

  /**
   * Upsert a dimension default (create or update)
   * @param {Object} data - The dimension default data
   * @returns {Promise} - API response
   */
  async upsertDimensionDefault(data) {
    try {
      const response = await http.post('/v1/dimension-defaults/upsert', data);
      return response.data;
    } catch (error) {
      console.error('Error upserting dimension default:', error);
      throw error;
    }
  }

  /**
   * Bulk upsert dimension defaults for a cube
   * @param {Object} data - The bulk upsert request data
   * @returns {Promise} - API response
   */
  async bulkUpsertDimensionDefaults(data) {
    try {
      const response = await http.post('/v1/dimension-defaults/bulk-upsert', data);
      return response.data;
    } catch (error) {
      console.error('Error bulk upserting dimension defaults:', error);
      throw error;
    }
  }

  /**
   * Update a dimension default by ID
   * @param {number} id - The dimension default ID
   * @param {Object} data - The updated data
   * @returns {Promise} - API response
   */
  async updateDimensionDefault(id, data) {
    try {
      const response = await http.put(`/v1/dimension-defaults/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating dimension default:', error);
      throw error;
    }
  }

  /**
   * Update a dimension default by cube and dimension
   * @param {number} cubeId - The cube ID
   * @param {number} dimensionId - The dimension (oracle_epm_metadata_id)
   * @param {Object} data - The updated data
   * @returns {Promise} - API response
   */
  async updateDimensionDefaultByCubeAndDimension(cubeId, dimensionId, data) {
    try {
      const response = await http.put(
        `/v1/dimension-defaults/cube/${cubeId}/dimension/${dimensionId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating dimension default by cube and dimension:', error);
      throw error;
    }
  }

  /**
   * Delete a dimension default by ID
   * @param {number} id - The dimension default ID
   * @returns {Promise} - API response
   */
  async deleteDimensionDefault(id) {
    try {
      const response = await http.delete(`/v1/dimension-defaults/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting dimension default:', error);
      throw error;
    }
  }

  /**
   * Delete a dimension default by cube and dimension
   * @param {number} cubeId - The cube ID
   * @param {number} dimensionId - The dimension (oracle_epm_metadata_id)
   * @returns {Promise} - API response
   */
  async deleteDimensionDefaultByCubeAndDimension(cubeId, dimensionId) {
    try {
      const response = await http.delete(
        `/v1/dimension-defaults/cube/${cubeId}/dimension/${dimensionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting dimension default by cube and dimension:', error);
      throw error;
    }
  }

  /**
   * Delete all dimension defaults for a cube
   * @param {number} cubeId - The cube ID
   * @returns {Promise} - API response
   */
  async deleteDimensionDefaultsByCube(cubeId) {
    try {
      const response = await http.delete(`/v1/dimension-defaults/cube/${cubeId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting dimension defaults by cube:', error);
      throw error;
    }
  }
}

export default new DimensionDefaultsService();

